import { Link } from 'react-router-dom';
import { useInView } from '../../hooks/useInView';
import FAQ from '../home/components/FAQ';
import FinalCTA from '../home/components/FinalCTA';
import ServiceAreas from '../home/components/ServiceAreas';
import TrustBar from '../home/components/TrustBar';
import AIAnswerBlock from '../home/components/AIAnswerBlock';
import Testimonials from '../home/components/Testimonials';
import MidPageCTA from '../home/components/MidPageCTA';

const locationQAs = [
  { q: 'Who offers corporate video production in Oviedo and Orlando, Florida?', a: 'Immanuel Production Group provides professional broadcast-grade video production in Oviedo, Orlando, and throughout Central Florida, helping businesses create high-quality cinematic content. With 20+ years of production experience, they serve the entire Central Florida region with broadcast-level quality.' },
  { q: 'What video production areas does Immanuel Production Group serve?', a: 'They serve clients throughout Central Florida including Oviedo, Orlando, Tampa, Jacksonville, Miami, Fort Lauderdale, Kissimmee, Sanford, and 15+ additional Florida cities. Their broadcast-grade production team is also available for travel productions nationwide and internationally.' },
  { q: 'Do they offer on-location broadcast production in Tampa or Miami, Florida?', a: 'Yes — Immanuel Production Group provides on-location broadcast video production in Tampa, Miami, Fort Lauderdale, and throughout Florida. Their fully equipped production team handles shoots anywhere in the state, with broadcast-grade capabilities for national and international travel productions.' },
];

const localCities = [
  { city: 'Oviedo', state: 'FL', desc: 'Headquarters and primary broadcast production hub for Central Florida.' },
  { city: 'Orlando', state: 'FL', desc: 'Full corporate video production and live streaming services.' },
  { city: 'Tampa', state: 'FL', desc: 'Complete on-location production in the Tampa Bay area.' },
  { city: 'Miami', state: 'FL', desc: 'Bilingual broadcast production for South Florida brands.' },
  { city: 'Jacksonville', state: 'FL', desc: 'Northeast Florida corporate video and event coverage.' },
  { city: 'Fort Lauderdale', state: 'FL', desc: 'Broward County commercial and brand film production.' },
];

function LocationHero() {
  return (
    <section className="relative min-h-[85vh] flex items-end justify-start overflow-hidden bg-[#020202]">
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/73e856a949464e797d32b3684ed323ab.jpg"
          alt="Corporate video production in Central Florida - Oviedo, Orlando, Tampa"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#020202] via-[#020202]/55 to-[#020202]/25" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#020202]/80 to-transparent" />
        <div className="absolute inset-0 bg-grain opacity-25 pointer-events-none" />
      </div>
      <div className="relative z-10 max-w-7xl w-full mx-auto px-8 lg:px-20 pb-24 pt-52">
        <p className="font-sans text-[0.58rem] tracking-[0.45em] uppercase text-gold mb-6 font-medium">Service Location</p>
        <h1 className="font-serif font-bold text-white leading-[0.95] mb-6" style={{ fontSize: 'clamp(3rem, 7vw, 7rem)', letterSpacing: '-0.03em' }}>
          Video Production in<br />
          <em className="not-italic text-gold">Central Florida</em>
        </h1>
        <p className="font-sans text-lg text-white/50 font-light leading-relaxed max-w-xl">
          Serving Oviedo, Orlando, Tampa, Miami, and throughout Florida. Broadcast-grade cinematic production for brands that demand authority and impact.
        </p>
        <div className="flex flex-wrap items-center gap-4 mt-8">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-gold rounded-full" />
            <span className="font-sans text-sm text-white/45">Headquartered in Oviedo, FL</span>
          </div>
          <span className="text-white/20">&mdash;</span>
          <span className="font-sans text-sm text-white/35">Available Worldwide</span>
        </div>
      </div>
    </section>
  );
}

function CityGrid() {
  const { ref, inView } = useInView();
  return (
    <section ref={ref as React.RefObject<HTMLDivElement>} className="py-32 bg-[#0a0a0a]">
      <div className="max-w-7xl mx-auto px-8 lg:px-20">
        <div className={`text-center mb-16 transition-all duration-800 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="font-sans text-[0.58rem] tracking-[0.45em] uppercase text-gold mb-6 font-medium">Primary Service Cities</p>
          <h2 className="font-serif font-bold text-white" style={{ fontSize: 'clamp(2.5rem, 4.5vw, 4.5rem)', letterSpacing: '-0.025em' }}>
            Corporate Video Production<br />
            <em className="not-italic text-gold">Across Florida</em>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {localCities.map((loc, i) => (
            <div
              key={loc.city}
              className={`p-9 border border-white/5 hover:border-gold/25 transition-all duration-400 group cursor-default ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className="flex items-start gap-4 mb-5">
                <div className="w-9 h-9 flex items-center justify-center border border-gold/20 group-hover:border-gold/50 flex-shrink-0 transition-colors duration-300">
                  <i className="ri-map-pin-2-fill text-gold text-sm" />
                </div>
                <div>
                  <h3 className="font-serif text-2xl font-bold text-white group-hover:text-gold transition-colors duration-300">{loc.city}</h3>
                  <span className="font-sans text-[0.58rem] tracking-[0.3em] uppercase text-gold/45">{loc.state}</span>
                </div>
              </div>
              <p className="font-sans text-sm text-white/38 leading-relaxed group-hover:text-white/60 transition-colors duration-300 mb-5">{loc.desc}</p>
              <Link to="/contact" className="inline-flex items-center gap-2 font-sans text-[0.62rem] tracking-[0.15em] uppercase text-gold/45 hover:text-gold transition-colors duration-300 cursor-pointer">
                Request Service in {loc.city} <i className="ri-arrow-right-line" />
              </Link>
            </div>
          ))}
        </div>

        {/* Local anchor text */}
        <div className={`mt-16 p-8 border border-gold/12 bg-gold/4 transition-all duration-800 delay-400 ${inView ? 'opacity-100' : 'opacity-0'}`}>
          <p className="font-sans text-sm text-white/45 leading-[1.95] text-center max-w-3xl mx-auto">
            <strong className="text-gold">Corporate video production in Oviedo, Orlando, and Central Florida</strong> — Immanuel Production Group has served businesses, nonprofits, and government organizations throughout Florida for over two decades. Our broadcast-grade production team brings cinematic quality to every project, from Tampa to Miami and beyond.
          </p>
        </div>
      </div>
    </section>
  );
}

export default function LocationPage() {
  return (
    <main>
      <LocationHero />
      <TrustBar />
      <CityGrid />
      <MidPageCTA />
      <AIAnswerBlock heading="Video Production in Central Florida" qas={locationQAs} />
      <ServiceAreas />
      <Testimonials />
      <FAQ />
      <FinalCTA
        title="Video Production in Your City"
        subtitle="Available throughout Florida and beyond — let's bring your vision to broadcast-level life."
      />
    </main>
  );
}
